import React, { useState } from 'react';
import { StockItem, ExpenseCategory, ExpenseTransaction } from '../../types';
import { db } from '../../db';

interface SyncQueueItem {
  type: string;
  action: string;
  data: unknown;
  status: string;
  timestamp: number;
}

export function useStock(
  addToSyncQueue: (item: SyncQueueItem) => Promise<unknown>,
  assertMutationAllowed: (fnName: string) => void,
  setDirtyStockIds: React.Dispatch<React.SetStateAction<Set<number>>>,
  expenseCategories: ExpenseCategory[],
  setExpenseCategories: React.Dispatch<React.SetStateAction<ExpenseCategory[]>>,
  addExpense: (item: ExpenseTransaction) => Promise<void>
) {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);

  const addStockItem = async (item: StockItem) => {
    assertMutationAllowed('addStockItem');
    const itemWithDocType = { ...item, docType: 'stockItem' as const };
    await db.stockItems.add(itemWithDocType);
    await db.syncStack.add({ type: 'stockItem' as any, action: 'create', data: itemWithDocType, status: 'pending', timestamp: Date.now() });
    setStockItems(prev => [...prev, itemWithDocType]);
  };

  const updateStockItem = async (item: StockItem, skipSync = false) => {
    assertMutationAllowed('updateStockItem');
    const normalizedItem = {
      ...item,
      critical: typeof item.criticalThreshold === 'number'
        ? item.stock <= item.criticalThreshold
        : item.stock <= 5
    };
    await db.stockItems.put(normalizedItem);
    if (!skipSync) {
      await addToSyncQueue({ type: 'stockItem', action: 'update', data: normalizedItem, status: 'pending', timestamp: Date.now() });
    } else {
      setDirtyStockIds(prev => {
        const next = new Set(prev).add(item.id);
        localStorage.setItem('dirtyStockIds', JSON.stringify(Array.from(next)));
        return next;
      });
    }
    setStockItems(prev => prev.map(i => i.id === item.id ? normalizedItem : i));
  };

  const deleteStockItem = async (id: number) => {
    await db.stockItems.delete(id);
    await db.syncStack.add({ type: 'stockItem' as any, action: 'delete', data: { id }, status: 'pending', timestamp: Date.now() });
    setStockItems(prev => prev.filter(i => i.id !== id));
  };

  const reportWastage = async (stockId: number, amount: number, reason: string) => {
    const stockItem = stockItems.find(s => s.id === stockId);
    if (!stockItem) return;

    const lossAmount = amount * stockItem.cost;
    const newStock = Math.max(0, stockItem.stock - amount);
    const updatedItem = { ...stockItem, stock: newStock };
    await updateStockItem(updatedItem);

    let catId: number;
    const existingCat = expenseCategories.find(c => c.isWastage || c.name.includes("Zaiyat") || c.name.includes("Fire"));

    if (existingCat) {
      catId = existingCat.id;
    } else {
      const newCat: Omit<ExpenseCategory, 'id'> = {
        name: "Zaiyat / Fire",
        description: "Otomatik zaiyat kaydı",
        isRecurring: false,
        isWastage: true,
      };
      const id = await db.expenseCategories.add(newCat as ExpenseCategory);
      catId = id as number;
      setExpenseCategories(prev => [...prev, { ...newCat, id: catId }]);
    }

    const expense: ExpenseTransaction = {
      id: Date.now() + 1,
      categoryId: catId,
      categoryName: "Zaiyat / Fire",
      amount: lossAmount,
      description: `Zaiyat: ${stockItem.name} (${amount} ${stockItem.unit}) - ${reason}`,
      date: new Date().toISOString()
    };

    await addExpense(expense);
  };

  return {
    stockItems,
    setStockItems,
    addStockItem,
    updateStockItem,
    deleteStockItem,
    reportWastage,
  };
}
